import React, { useMemo, useState } from 'react'
import { IStableAverage } from '../../shared/types/Stabledata.type'
import { TinsightParam } from '../../shared/types/computedKeyParam.type'
import {
    Box,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material'
import { t } from 'i18next'
import palette from '../../theme/palette'
import { flatMap, groupBy, isEmpty, upperCase } from 'lodash'
import { trackSurface } from '../../shared/types/tracks.type'
import { Icon } from '@iconify/react'

interface StableAvgPageProps {
    data: IStableAverage
    insightParams: TinsightParam[]
}

const StableAvgPage: React.FC<StableAvgPageProps> = ({ data, insightParams }) => {
    const [customColorIndexes, setCustomColorsIndexes] = useState<{ row: number, col: number, color?: string | never, highlighted?: boolean | never }[]>([]);



    const columns = [
        t('eap:table:headers:trackName'),
        t('stable:countTrainings'),
        ...data.averages.map(avg => {
            let param = insightParams.find(ip => ip.name === avg.parameter_name)
            if (!param) return ""
            return [param.name_translated, `(${param.unit_translated})`].join(' ')
        })
    ]



    const rows = useMemo(() => {
        const newCustomColorIndexes: typeof customColorIndexes = [];
        let currentIndex = 0;
        
        const groupedBySurface = groupBy(
            data.track_averages.filter(trackAvg => trackAvg?.track_surface != null),
            trackAvg =>
                typeof trackAvg.track_surface === 'string'
                    ? trackAvg?.track_surface
                    : trackAvg?.track_surface?.id
        );

        
        
        

        const flattened = flatMap(groupedBySurface, (group, surfaceId) => {
            // Surface header row
            
            const findSurface = data.track_surface_averages.find(
                surface => surface?.track_surface?.id === Number(surfaceId)
            );

            const customRow = [
                upperCase(findSurface?.track_surface?.display_name || ""),
                findSurface?.count_trainings,
                ...(findSurface ? findSurface.averages.map((av, colIndex) => {
                    if (av.color) {
                        newCustomColorIndexes.push({
                            row: currentIndex,
                            col: colIndex + 2,
                            color: av.color,
                        });
                    }

                    return av.formatted_value
                }) : []),
            ];

            // ✅ Push custom row color for first column
            newCustomColorIndexes.push({
                row: currentIndex,
                col: 0,
                color: palette.light.primary.lighter,
            });

            currentIndex += 1;

            // Map tracks for this surface
            const surfaceRows = group.map(trackAvg => {
                const rowIndex = currentIndex;

                const row = [
                    trackAvg.track_name.display_name,
                    trackAvg.count_trainings,
                    ...trackAvg.averages.map((av, colIndex) => {
                        if (av.color) {
                            newCustomColorIndexes.push({
                                row: rowIndex,
                                col: colIndex + 2,
                                color: av.color,
                            });
                        }
                        if (av.highlighted) {
                            newCustomColorIndexes.push({
                                row: rowIndex,
                                col: colIndex + 2,
                                highlighted: true
                            });
                        }
                        return av.formatted_value;
                    }),
                ];

                currentIndex += 1;
                return row;
            });

            return [customRow, ...surfaceRows];
        });



        // Add total row
        flattened.push([
            t('avgGeneral'),
            data.count_trainings,
            ...data.averages.map(avg => avg.formatted_value),
        ]);



        // Update color state only
        setCustomColorsIndexes(newCustomColorIndexes);

        return flattened;
    }, [data.track_averages, data.track_surface_averages, data.averages, t]);




    const getColor = (row: number, cell: number) => {
        if (isEmpty(customColorIndexes)) return undefined

        let findColor = customColorIndexes.find(custom => custom.row === row && custom.col === cell)

        return findColor && "color" in findColor ? findColor.color : undefined

    }
    const isHighlighted = (row: number, cell: number) => {
        if (isEmpty(customColorIndexes)) return undefined

        let findHl = customColorIndexes.find(custom => custom.row === row && custom.col === cell)

        return findHl && findHl?.highlighted
    }

    const isTrackSurfaceRow = (row: number) => {
        if (isEmpty(customColorIndexes)) return undefined


        return customColorIndexes.find(f => f.row === row && f.col === 0)
    }

    return (
        <>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
                mb={3}
            >
                <Typography variant="h4" sx={{ color: palette.light.primary.main }}>
                    {t(`stable:title:${data.type}`)}
                </Typography>
                <img
                    style={{ objectFit: 'contain', width: 55, height: 55, marginRight: 15 }}
                    src="/arioneo_logo_white.png"
                    alt="arioneo logo"
                />
            </Box>
            <Typography sx={{ lineHeight: '1' }} color={'gray'} variant='caption'>{t('stable:avgStableExplication')}</Typography>

            <TableContainer component={Paper} sx={{ mt: 1, borderRadius: 0, boxShadow: 1, tableLayout: 'auto' }}>
                <Table
                    sx={{
                        borderCollapse: 'separate',
                        width: '100%',
                        '& td, & th': {
                            fontSize: '12px',
                        },
                        '& th': {
                            lineHeight: '1.1',
                        }
                    }}
                    size="small" aria-label="dense table" >
                    <TableHead
                        sx={{
                            '& td, & th': {
                                borderBottom: 'none',
                                boxSizing: 'border-box',
                            },
                        }}
                    >
                        <TableRow
                            sx={{
                                background: `linear-gradient(to right, ${palette.light.primary.darker}, ${palette.light.primary.main})`,
                            }}
                        >
                            {columns.map((col, index) => (
                                <TableCell key={col}
                                    sx={{
                                        py: 0.25,
                                        px: 0.5,
                                        fontWeight: 600,
                                        color: 'white',
                                        textAlign: "center"
                                    }}
                                >
                                    {col}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody
                        sx={{
                            width: '100%',
                            '& td, & th': {
                                padding: '3px !important',
                                boxSizing: 'border-box',

                            },

                        }}
                    >
                        {rows.map((row, rowIndex) => (
                            <TableRow
                                className='listItemExam'
                                sx={{
                                    backgroundColor: rowIndex === rows.length - 1 ? `${palette.light.primary.light} !important` : undefined,
                                    boxShadow: isTrackSurfaceRow(rowIndex) ? "rgba(33, 35, 38, 0.1) 0px 10px 10px -10px" : 'none',

                                }}
                                key={rowIndex}>
                                {row.map((cell, cellIndex) => (
                                    <TableCell
                                        sx={{
                                            borderCollapse: 'separate',
                                            backgroundColor: getColor(rowIndex, cellIndex) ?? 'transparent !important',
                                            textAlign: 'center',
                                            fontWeight: isTrackSurfaceRow(rowIndex) || rowIndex === rows.length - 1 ? 600 : 'initial',
                                            borderTop: rowIndex !== 0 && isTrackSurfaceRow(rowIndex) ? `4px solid ${palette.light.primary.darker}` : undefined,
                                            // border: isHighlighted(rowIndex, cellIndex) ? `1px solid ${palette.light.primary.darker}` : undefined,
                                            fontStyle: !isTrackSurfaceRow(rowIndex) && cellIndex === 0 && rowIndex !== rows.length - 1 ? "italic" : "initial",
                                            breakInside: 'avoid',
                                            fontSize: 12,
                                        }}
                                        key={cellIndex}>
                                        <span style={{
                                            border: isHighlighted(rowIndex, cellIndex) ? `1px solid ${palette.light.primary.darker}` : undefined,
                                            padding: '2px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-evenly',
                                            
                                        }}>
                                            {cell} {isTrackSurfaceRow(rowIndex) && cellIndex === 0 ? <Icon icon={'uis:angle-down'} fontSize={14} /> : null}
                                        </span>
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}

export default StableAvgPage
